// frontend/modules/customer-category/views/CustomerOnboardingView.tsx
import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Steps,
    Button,
    message,
    Row,
    Col,
    Space
} from 'antd';
import {
    UserOutlined,
    InfoCircleOutlined,
    AppstoreOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useCustomerOnboarding } from '../hooks/useCustomerOnboarding';
import CategorySelectionStep from '../components/onboarding/CategorySelectionStep';
import GeneralInfoStep from '../components/onboarding/GeneralInfoStep';
import AdditionalInfoStep from '../components/onboarding/AdditionalInfoStep';
import ProductsStep from '../components/onboarding/ProductsStep';
import SummaryStep from '../components/onboarding/SummaryStep';
import { CustomerOnboardingData } from '../types';

const { Title } = Typography;
const { Step } = Steps;

const CustomerOnboardingView: React.FC = () => {
    const { dark } = useTheme();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<CustomerOnboardingData>({
        isFreestyle: false,
        customerData: {},
        custInfoData: {},
        selectedProducts: []
    });

    const {
        userCategoryAccess,
        selectedCategory,
        isLoading,
        fetchUserCategoryAccess,
        fetchCategoryById,
        createCustomer
    } = useCustomerOnboarding();

    useEffect(() => {
        fetchUserCategoryAccess();
    }, []); // Remove fetchUserCategoryAccess from dependencies to prevent infinite loops

    // When a category is selected, fetch its details
    useEffect(() => {
        if (formData.categoryId) {
            fetchCategoryById(formData.categoryId);
        }
    }, [formData.categoryId]); // Remove fetchCategoryById from dependencies to prevent infinite loops

    const steps = [
        {
            title: 'Select Category',
            content: <CategorySelectionStep
                userCategoryAccess={userCategoryAccess}
                formData={formData}
                onUpdateFormData={(data) => setFormData(prev => ({ ...prev, ...data }))}
            />,
            icon: <UserOutlined />
        },
        {
            title: 'General Info',
            content: <GeneralInfoStep
                category={selectedCategory}
                formData={formData}
                onUpdateFormData={(data) => setFormData(prev => ({ ...prev, ...data }))}
            />,
            icon: <UserOutlined />
        },
        {
            title: 'Additional Info',
            content: <AdditionalInfoStep
                category={selectedCategory}
                formData={formData}
                onUpdateFormData={(data) => setFormData(prev => ({ ...prev, ...data }))}
            />,
            icon: <InfoCircleOutlined />
        },
        {
            title: 'Products',
            content: <ProductsStep
                category={selectedCategory}
                formData={formData}
                onUpdateFormData={(data) => setFormData(prev => ({ ...prev, ...data }))}
            />,
            icon: <AppstoreOutlined />
        },
        {
            title: 'Summary',
            content: <SummaryStep
                category={selectedCategory}
                formData={formData}
            />,
            icon: <CheckCircleOutlined />
        }
    ];

    const next = () => {
        setCurrentStep(currentStep + 1);
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = async () => {
        try {
            await createCustomer(formData);
            message.success('Customer created successfully');
            // Redirect to customer list or another page
        } catch (error) {
            message.error('Failed to create customer');
        }
    };

    return (
        <div className="space-y-6">
            <Title level={2} className={dark ? 'text-white' : ''}>
                Customer Onboarding
            </Title>

            <Card className={dark ? 'bg-gray-800' : ''}>
                <Steps current={currentStep} className="mb-6">
                    {steps.map(item => (
                        <Step key={item.title} title={item.title} icon={item.icon} />
                    ))}
                </Steps>

                <div className="p-4 min-h-80">
                    {steps[currentStep].content}
                </div>

                <Row justify="end" className="mt-6">
                    <Space>
                        {currentStep > 0 && (
                            <Button onClick={prev}>
                                Previous
                            </Button>
                        )}

                        {currentStep < steps.length - 1 && (
                            <Button type="primary" onClick={next}>
                                Next
                            </Button>
                        )}

                        {currentStep === steps.length - 1 && (
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={isLoading}
                            >
                                Submit
                            </Button>
                        )}
                    </Space>
                </Row>
            </Card>
        </div>
    );
};

export default CustomerOnboardingView;